import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Truck, Calendar, Zap, Navigation, Activity, Wrench, CreditCard, ArrowRight, Bell } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminOperationsHub() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [planning, weekend, agenda] = await Promise.all([
        fetch(`${BACKEND}/api/admin/livraisons/planning?semaines=2`, { headers: H }).then(r => r.json()).catch(() => ({})),
        fetch(`${BACKEND}/api/admin/disponibilites/weekend`, { headers: H }).then(r => r.json()).catch(() => ({})),
        fetch(`${BACKEND}/api/agenda/stats`, { headers: H }).then(r => r.json()).catch(() => ({})),
      ]);
      setData({ planning, weekend, agenda });
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
    setLoading(false);
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const planningStats = data?.planning?.stats || {};
  const weekendData = data?.weekend || {};

  const kpis = [
    { icon: <Truck size={18}/>, color: 'var(--admin-teal)', label: 'Livraisons aujourd\'hui', value: planningStats.aujourd_hui ?? 0, to: '/admin/livraisons' },
    { icon: <Calendar size={18}/>, color: 'var(--admin-blue)', label: 'Cette semaine', value: planningStats.cette_semaine ?? 0, sub: 'livraisons à venir', to: '/admin/livraisons' },
    { icon: <Bell size={18}/>, color: 'var(--admin-amber)', label: 'À notifier J-7', value: planningStats.a_notifier_J7 ?? 0, sub: 'clients non contactés', to: '/admin/livraisons' },
    { icon: <Zap size={18}/>, color: 'var(--admin-green)', label: 'Produits libres weekend', value: weekendData.total_libres ?? 0, sub: `${weekendData.weekend?.samedi||''} — ${weekendData.weekend?.dimanche||''}`, to: '/admin/weekend-offres' },
  ];

  const shortcuts = [
    { to: '/admin/livraisons', icon: <Truck size={14}/>, label: 'Calendrier livraisons', color: 'var(--admin-teal)' },
    { to: '/admin/disponibilites', icon: <Calendar size={14}/>, label: 'Disponibilités', color: 'var(--admin-blue)' },
    { to: '/admin/weekend-offres', icon: <Zap size={14}/>, label: 'Offres weekend', color: 'var(--admin-green)' },
    { to: '/admin/tournees', icon: <Navigation size={14}/>, label: 'Tournées', color: 'var(--admin-gold)' },
    { to: '/admin/etat-materiel', icon: <Wrench size={14}/>, label: 'Matériel', color: 'var(--admin-amber)' },
    { to: '/admin/caution-avancee', icon: <CreditCard size={14}/>, label: 'Cautions', color: 'var(--admin-purple)' },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--admin-teal)' }}>🚚 Opérations</h1>
        <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Livraisons, disponibilités, terrain et suivi</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-32 rounded-2xl" style={{ background: 'var(--admin-surface)' }} />)}</div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpis.map((k, i) => (
            <Link key={i} to={k.to} className="block p-5 rounded-2xl transition-all hover:opacity-90"
              style={{ background: 'var(--admin-surface)', border: `1px solid ${k.color}20` }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${k.color}15`, color: k.color }}>{k.icon}</div>
              <p className="text-2xl font-bold mb-0.5" style={{ color: k.color }}>{k.value}</p>
              <p className="text-sm font-semibold" style={{ color: 'var(--admin-text)' }}>{k.label}</p>
              {k.sub && <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>{k.sub}</p>}
            </Link>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {shortcuts.map((s, i) => (
          <Link key={i} to={s.to} className="flex items-center gap-2 p-3 rounded-xl transition-all hover:opacity-80"
            style={{ background: 'var(--admin-surface)', border: `1px solid ${s.color}20` }}>
            <span style={{ color: s.color }}>{s.icon}</span>
            <span className="text-xs font-medium" style={{ color: 'var(--admin-text)' }}>{s.label}</span>
          </Link>
        ))}
      </div>

      {/* Prochaines livraisons */}
      {data?.planning?.livraisons?.length > 0 && (
        <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
          <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--admin-border-soft)' }}>
            <h2 className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>Prochaines livraisons</h2>
            <Link to="/admin/livraisons" className="text-xs" style={{ color: 'var(--admin-teal)' }}>Tout voir →</Link>
          </div>
          {data.planning.livraisons.slice(0,5).map((l, i) => (
            <div key={l.quote_id} className="flex items-center gap-4 px-5 py-3" style={{ borderBottom: i<4 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate" style={{ color: 'var(--admin-text)' }}>{l.client_nom}</p>
                <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>{l.date_evenement} · {l.adresse?.slice(0,30)}</p>
              </div>
              <div className="text-right">
                {l.jours_restants === 0 ? <span style={{ color: 'var(--admin-green)', fontSize: 11 }}>Aujourd'hui</span>
                  : l.jours_restants < 0 ? <span style={{ color: 'var(--admin-red)', fontSize: 11 }}>Passé</span>
                  : <span style={{ color: 'var(--admin-blue)', fontSize: 11 }}>J-{l.jours_restants}</span>}
                {l.alertes?.includes('J-7') && !l.notifs_envoyees?.includes('J-7') && (
                  <span className="ml-2 text-xs" style={{ color: 'var(--admin-amber)' }}>⚠ J-7</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
